import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Nav,
    Pagination,
    Row,
    Spinner,
    Table,
} from "react-bootstrap";
import axios from "axios";
import { TranslatorContext } from "../../../../context/Translator";
import PageLayout from "../../../../layouts/PageLayout";
import AddPaymentModal from "../AddPaymentModal";
import PaymentApprovalButton from "../PaymentApprovalButton";

const PAGE_SIZE = 20;

const formatAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount)
        ? amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "0.00";
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? "-"
        : date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const getDeliveryStatusVariant = (status) => {
    switch ((status || "").toLowerCase()) {
        case "delivered":
            return "success";
        case "pending":
            return "warning";
        case "returned":
            return "danger";
        default:
            return "secondary";
    }
};

const getPaymentStatusVariant = (status) => {
    switch ((status || "").toLowerCase()) {
        case "approved":
            return "success";
        case "cancelled":
            return "danger";
        default:
            return "warning";
    }
};

function OrdersPagination({ pagination, onPageChange, t }) {
    if (pagination.last_page <= 1) return null;

    const pages = [];
    const firstVisible = Math.max(1, pagination.current_page - 1);
    const lastVisible = Math.min(pagination.last_page, pagination.current_page + 1);

    pages.push(
        <Pagination.Prev
            key="previous"
            disabled={pagination.current_page <= 1}
            onClick={() => onPageChange(pagination.current_page - 1)}
        />
    );
    if (firstVisible > 1) {
        pages.push(<Pagination.Item key={1} onClick={() => onPageChange(1)}>1</Pagination.Item>);
        if (firstVisible > 2) pages.push(<Pagination.Ellipsis key="left-ellipsis" disabled />);
    }
    for (let page = firstVisible; page <= lastVisible; page += 1) {
        pages.push(
            <Pagination.Item
                key={page}
                active={page === pagination.current_page}
                onClick={() => onPageChange(page)}
            >
                {page}
            </Pagination.Item>
        );
    }
    if (lastVisible < pagination.last_page) {
        if (lastVisible < pagination.last_page - 1) {
            pages.push(<Pagination.Ellipsis key="right-ellipsis" disabled />);
        }
        pages.push(
            <Pagination.Item
                key={pagination.last_page}
                onClick={() => onPageChange(pagination.last_page)}
            >
                {pagination.last_page}
            </Pagination.Item>
        );
    }
    pages.push(
        <Pagination.Next
            key="next"
            disabled={pagination.current_page >= pagination.last_page}
            onClick={() => onPageChange(pagination.current_page + 1)}
        />
    );

    return (
        <Card.Footer className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
            <small className="text-muted">
                {t("Showing")} {pagination.from || 0} {t("to")} {pagination.to || 0} {t("of")} {pagination.total} {t("orders")}
            </small>
            <Pagination size="sm" className="mb-0">{pages}</Pagination>
        </Card.Footer>
    );
}

export default function MarketPlacerDetailPage() {
    const { id: marketPlacerId } = useParams();
    const { t } = useContext(TranslatorContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [activeTab, setActiveTab] = useState("summary");
    const [activeOrderTab, setActiveOrderTab] = useState("delivered");
    const [marketPlacerInfo, setMarketPlacerInfo] = useState(null);
    const [summary, setSummary] = useState(null);
    const [orders, setOrders] = useState([]);
    const [payments, setPayments] = useState([]);
    const [banks, setBanks] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: PAGE_SIZE,
        total: 0,
        from: 0,
        to: 0,
    });
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const fetchMarketPlacerDetails = useCallback(async (page = 1) => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/market-placer/${marketPlacerId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        Accept: "application/json",
                    },
                    params: { page, per_page: PAGE_SIZE },
                }
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load market placer ledger.");
            }

            const data = response.data?.data || {};
            setMarketPlacerInfo(data.mplacer || null);
            setSummary(data.summary || null);
            setOrders(Array.isArray(data.orders) ? data.orders : []);
            setPayments(Array.isArray(data.payments) ? data.payments : []);
            if (data.pagination) {
                setPagination({
                    current_page: data.pagination.current_page || page,
                    last_page: data.pagination.last_page || 1,
                    per_page: data.pagination.per_page || PAGE_SIZE,
                    total: data.pagination.total || 0,
                    from: data.pagination.from || 0,
                    to: data.pagination.to || 0,
                });
            }
        } catch (err) {
            console.error("Error fetching market placer ledger:", err);
            setError(err.response?.data?.message || err.message || "Failed to fetch market placer ledger.");
            setMarketPlacerInfo(null);
            setSummary(null);
            setOrders([]);
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }, [marketPlacerId]);

    const fetchBanks = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/banks`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            });
            if (response.data?.success) setBanks(response.data.data || []);
        } catch (err) {
            console.error("Error fetching banks:", err);
            setError((current) => current || err.response?.data?.message || "Failed to fetch banks.");
        }
    }, []);

    useEffect(() => {
        fetchMarketPlacerDetails(currentPage);
    }, [fetchMarketPlacerDetails, currentPage]);

    useEffect(() => {
        fetchBanks();
    }, [fetchBanks]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) setCurrentPage(page);
    };

    const handlePaymentSuccess = (message) => {
        setSuccessMsg(message || "Payment added successfully.");
        fetchMarketPlacerDetails(currentPage);
    };

    const settlementType = (summary?.settlement_type || "").toLowerCase();
    const mplacerPays = summary?.payment_direction === "receive" || settlementType === "receivable";
    const companyPays = summary?.payment_direction === "pay" || settlementType === "payable";
    const summaryLabels = mplacerPays
        ? {
            total: "Total amount owed by Market Placer",
            settled: "Amount already paid by Market Placer",
            remaining: "Amount still owed by Market Placer",
            extra: "Extra amount paid by Market Placer",
        }
        : companyPays
            ? {
                total: "Total amount payable to Market Placer",
                settled: "Amount already paid to Market Placer",
                remaining: "Amount still payable to Market Placer",
                extra: "Extra amount paid to Market Placer",
            }
            : {
                total: "Total amount",
                settled: "Settled amount",
                remaining: "Remaining amount",
                extra: "Extra amount",
            };

    const visibleOrders = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        return orders.filter((order) => {
            const isDelivered = (order.delivery_status || "").toLowerCase() === "delivered";
            const matchesTab = activeOrderTab === "delivered" ? isDelivered : !isDelivered;
            const matchesSearch = !keyword || [
                order.order_number,
                order.product_code,
                order.tracking_number,
                order.marketplace_name,
            ].some((value) => String(value || "").toLowerCase().includes(keyword));
            return matchesTab && matchesSearch;
        });
    }, [orders, activeOrderTab, search]);

    const orderCounts = useMemo(() => ({
        delivered: orders.filter((order) => (order.delivery_status || "").toLowerCase() === "delivered").length,
        others: orders.filter((order) => (order.delivery_status || "").toLowerCase() !== "delivered").length,
    }), [orders]);

    return (
        <PageLayout>
            <Row className="mb-4">
                <Col xl={12}>
                    <Card className="border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)" }}>
                        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-4">
                            <div>
                                <h3 className="fw-bold mb-1">{marketPlacerInfo?.name || t("Market Placer Profile")}</h3>
                                <div className="text-white-50">{marketPlacerInfo?.email || t("Market Placer ledger and payment details")}</div>
                                <div className="small text-white-50 mt-2">
                                    <Link to="/market-placer" className="text-white-50 text-decoration-none">{t("Market Placers")}</Link>
                                    {" / "}{t("Ledger Details")}
                                    {marketPlacerInfo?.payment_type && ` · ${t(marketPlacerInfo.payment_type.replaceAll("_", " "))}`}
                                </div>
                            </div>
                            {summary?.who_adds_payment === "admin" ? (
                                <Button
                                    variant="success"
                                    className="d-flex align-items-center gap-2 px-4 py-2 fw-semibold"
                                    onClick={() => setShowPaymentModal(true)}
                                    disabled={loading}
                                >
                                    <span className="material-icons">add_card</span>
                                    {t("Pay Market Placer")}
                                </Button>
                            ) : summary?.who_adds_payment === "self" ? (
                                <div className="text-white small text-end" style={{ maxWidth: "320px" }}>
                                    {t("Receivable: the Market Placer adds the payment they send from their portal. Approve or reject it in the Payments tab.")}
                                    {Number(summary?.pending_amount || 0) > 0 && (
                                        <div className="fw-semibold mt-1">
                                            {t("Waiting for approval")}: PKR {Number(summary.pending_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </div>
                                    )}
                                </div>
                            ) : summary ? (
                                <div className="text-white-50 small">{t("Set payable or receivable on the profile to add payments.")}</div>
                            ) : null}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
            {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg("")}>{successMsg}</Alert>}

            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom-0 pt-3">
                    <Nav variant="tabs" activeKey={activeTab} onSelect={(tab) => tab && setActiveTab(tab)}>
                        <Nav.Item><Nav.Link eventKey="summary">{t("Summary")}</Nav.Link></Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="orders">
                                {t("Orders")} ({pagination.total})
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item><Nav.Link eventKey="payments">{t("Payments")} ({payments.length})</Nav.Link></Nav.Item>
                    </Nav>
                </Card.Header>

                {loading ? (
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <div className="small text-muted mt-2">{t("Loading market placer ledger...")}</div>
                    </Card.Body>
                ) : (
                    <>
                        {activeTab === "summary" && (
                            <Card.Body className="pt-4">
                                {summary ? (
                                    <Row className="justify-content-center">
                                        <Col lg={8} xl={7}>
                                            <Card className="border shadow-sm">
                                                <Card.Body className="p-0">
                                                    <div className={`p-4 border-bottom ${mplacerPays ? "bg-warning-subtle" : companyPays ? "bg-success-subtle" : "bg-light"}`}>
                                                        <div className="small text-muted mb-1">
                                                            {t("Settlement summary")}
                                                            {summary.settlement_type && ` · ${t(summary.settlement_type)}`}
                                                        </div>
                                                        <h4 className={`fw-bold mb-1 ${mplacerPays ? "text-warning-emphasis" : companyPays ? "text-success" : "text-dark"}`}>
                                                            {mplacerPays
                                                                ? t("Market Placer owes the company")
                                                                : companyPays
                                                                    ? t("Company owes the Market Placer")
                                                                    : t("Market Placer account balance")}
                                                        </h4>
                                                        <div className="text-muted">
                                                            {summary.extra_amount > 0
                                                                ? t(mplacerPays
                                                                    ? "Market Placer has paid more than the amount owed to the company."
                                                                    : companyPays
                                                                        ? "The company has paid more than the amount owed to the Market Placer."
                                                                        : "The account has an extra payment.")
                                                                : summary.remaining_amount > 0
                                                                    ? t(mplacerPays
                                                                        ? "Market Placer still owes this amount to the company."
                                                                        : companyPays
                                                                            ? "The company still owes this amount to the Market Placer."
                                                                            : "This amount is still unsettled.")
                                                                    : t("The account is fully settled.")}
                                                        </div>
                                                    </div>
                                                    <div className="px-4">
                                                        {[
                                                            { label: summaryLabels.total, value: summary.total_amount, emphasized: true },
                                                            { label: summaryLabels.settled, value: summary.settled_amount },
                                                            { label: summaryLabels.remaining, value: summary.remaining_amount, emphasized: true },
                                                            { label: summaryLabels.extra, value: summary.extra_amount },
                                                            { label: "Amount on hold for pending orders", value: summary.hold_amount },
                                                            { label: "Opening balance", value: summary.opening_balance },
                                                            { label: "Payment method", value: summary.payment_type ? t(summary.payment_type.replaceAll("_", " ")) : "-", text: true },
                                                            { label: "Payment value", value: summary.payment_value },
                                                            { label: "Delivered orders", value: summary.delivered_orders, count: true },
                                                            { label: "Pending orders", value: summary.pending_orders, count: true },
                                                        ].map((item, index, items) => (
                                                            <div
                                                                key={item.label}
                                                                className={`d-flex justify-content-between align-items-center gap-3 py-3 ${index < items.length - 1 ? "border-bottom" : ""}`}
                                                            >
                                                                <span className={item.emphasized ? "fw-semibold text-dark" : "text-muted"}>
                                                                    {t(item.label)}
                                                                </span>
                                                                <span className={`text-end ${item.emphasized ? "fw-bold text-dark fs-5" : "fw-semibold text-dark"}`}>
                                                                    {item.count
                                                                        ? item.value ?? 0
                                                                        : item.text
                                                                            ? item.value
                                                                            : `PKR ${formatAmount(item.value)}`}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                ) : (
                                    <div className="text-center text-muted py-4">{t("No summary data available.")}</div>
                                )}
                            </Card.Body>
                        )}

                        {activeTab === "orders" && (
                            <>
                                <div className="px-3 pt-3">
                                    <Nav
                                        variant="pills"
                                        activeKey={activeOrderTab}
                                        onSelect={(tab) => tab && setActiveOrderTab(tab)}
                                        className="gap-2"
                                    >
                                        <Nav.Item>
                                            <Nav.Link eventKey="delivered">{t("Delivered")} ({orderCounts.delivered})</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="others">{t("Others")} ({orderCounts.others})</Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </div>
                                <Card.Body className="pb-3">
                                    <Row className="g-3 align-items-end">
                                        <Col md={6} lg={5}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold text-muted">{t("Search this page")}</Form.Label>
                                                <Form.Control
                                                    type="search"
                                                    value={search}
                                                    onChange={(event) => setSearch(event.target.value)}
                                                    placeholder={t("Order number, product code, tracking...")}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col className="small text-muted">
                                            {t("Search and delivery tabs apply to orders on the current page.")}
                                        </Col>
                                    </Row>
                                </Card.Body>
                                <div className="table-responsive">
                                    <Table hover className="mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{t("Order #")}</th>
                                                <th>{t("Product Code")}</th>
                                                <th>{t("Marketplace")}</th>
                                                <th>{t("Delivery Status")}</th>
                                                <th>{t("Tracking Number")}</th>
                                                <th className="text-end">{t("Sell Price (USD)")}</th>
                                                <th className="text-end">{t("Shipping (PKR)")}</th>
                                                <th className="text-end">{t("Company Amount (USD)")}</th>
                                                <th className="text-end">{t("Company Amount (PKR)")}</th>
                                                <th className="text-end">{t("Market Placer Earning (USD)")}</th>
                                                <th className="text-end">{t("Market Placer Earning (PKR)")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleOrders.length ? visibleOrders.map((order) => (
                                                <tr key={order.id}>
                                                    <td className="fw-semibold">{order.order_number || "-"}</td>
                                                    <td>{order.product_code || "-"}</td>
                                                    <td>{order.marketplace_name || "-"}</td>
                                                    <td>
                                                        <Badge bg={getDeliveryStatusVariant(order.delivery_status)}>
                                                            {t(order.delivery_status || "Unknown")}
                                                        </Badge>
                                                    </td>
                                                    <td>{order.tracking_number || "-"}</td>
                                                    <td className="text-end">USD {formatAmount(order.sell_price_usd)}</td>
                                                    <td className="text-end">PKR {formatAmount(order.shipping_pkr)}</td>
                                                    <td className="text-end">USD {formatAmount(order.company_amount_usd)}</td>
                                                    <td className="text-end fw-semibold">PKR {formatAmount(order.company_amount_pkr)}</td>
                                                    <td className="text-end">USD {formatAmount(order.earning_usd)}</td>
                                                    <td className="text-end fw-semibold">PKR {formatAmount(order.earning_pkr)}</td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan={11} className="text-center text-muted py-5">{t("No orders found on this page.")}</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                                <OrdersPagination pagination={pagination} onPageChange={handlePageChange} t={t} />
                            </>
                        )}

                        {activeTab === "payments" && (
                            <Card.Body>
                                <div className="mb-3">
                                    <h5 className="fw-bold mb-1">{t("Payment history")}</h5>
                                    <small className="text-muted">
                                        {t("Only approved payments count toward the settlement; pending or cancelled payments do not.")}
                                    </small>
                                </div>
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{t("Payment Date")}</th>
                                                <th>{t("Status")}</th>
                                                <th>{t("Payment Type")}</th>
                                                <th>{t("Bank")}</th>
                                                <th>{t("Recorded By")}</th>
                                                <th>{t("Notes")}</th>
                                                <th className="text-end">{t("Amount")}</th>
                                                <th>{t("Action")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.length ? payments.map((payment) => (
                                                <tr key={payment.id}>
                                                    <td>{formatDate(payment.payment_date)}</td>
                                                    <td>
                                                        <Badge bg={getPaymentStatusVariant(payment.status)}>
                                                            {t(payment.status || "pending")}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-capitalize">{t(payment.payment_type || "-")}</td>
                                                    <td>{payment.bank?.name || "-"}</td>
                                                    <td>{payment.creator?.name || "-"}</td>
                                                    <td>{payment.notes || "-"}</td>
                                                    <td className="text-end fw-semibold">PKR {formatAmount(payment.amount)}</td>
                                                    <td>
                                                        <PaymentApprovalButton
                                                            payment={payment}
                                                            onApproved={() => fetchMarketPlacerDetails(currentPage)}
                                                            t={t}
                                                        />
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan={8} className="text-center text-muted py-5">{t("No payment history found.")}</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        )}
                    </>
                )}
            </Card>

            <AddPaymentModal
                show={showPaymentModal}
                onHide={() => setShowPaymentModal(false)}
                listerId={marketPlacerId}
                listerInfo={marketPlacerInfo}
                entityType="market-placer"
                banks={banks}
                t={t}
                onSuccess={handlePaymentSuccess}
            />

        </PageLayout>
    );
}
